import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import type { PrDetailBody } from '../../shared/pr/api';
import {
  stagesFor,
  type ActionId,
  type NextStep,
  type Stage,
  type StageView,
  type Viewer,
} from '../../shared/pr/workspace';
import { useI18n, type MessageKey } from '../i18n';
import { apiGet } from '../lib/api';
import { formatDate } from '../lib/dates';
import { StatusPill, statusKey } from './StatusPill';

// The request workspace (D98): the same header and stage tabs on the request page and on every
// stage page (quotes, PO, delivery, invoice, payments), so a request reads as one place.

const money = (currency: string, amount: number) =>
  `${currency} ${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)}`;

/** The cost centre charged, with the requester's own vertical when it differs (D105). */
export function chargedText(
  pr: Pick<PrDetailBody, 'vertical' | 'requesterVertical'>,
  t: (k: MessageKey, v?: Record<string, string>) => string,
): string {
  if (!pr.vertical) return '—';
  return pr.requesterVertical && pr.requesterVertical !== pr.vertical
    ? `${pr.vertical} (${t('req.f.raisedFrom', { v: pr.requesterVertical })})`
    : pr.vertical;
}

/** PR number large, status, title and the labelled facts everyone asks for first. */
export function RequestHeader({ pr }: { pr: PrDetailBody }) {
  const { t, lang } = useI18n();
  const facts: [MessageKey, string, boolean?][] = [
    ['req.f.requester', pr.requesterName || pr.requesterUPN],
    ['req.f.department', pr.department || '—'],
    ['req.f.chargedTo', chargedText(pr, t)],
    ['pr.profile.entity', pr.entity, true],
    ['req.f.raised', pr.created ? formatDate(pr.created, lang) : '—'],
    ['req.f.neededBy', pr.neededBy ? formatDate(pr.neededBy, lang) : '—'],
    ['req.f.budget', pr.estBudget === null ? '—' : money(pr.currency, pr.estBudget), true],
  ];
  return (
    <section class="card ws-head" aria-label={t('ws.summary')}>
      <div class="ws-head__top">
        <span class="ws-ref" dir="ltr">
          {pr.prNo}
        </span>
        <StatusPill status={pr.status} own={pr.canEdit} />
        {pr.rush && <span class="pill pill--warning">{t('req.rush')}</span>}
      </div>
      <h2 class="ws-title" dir="auto">
        {pr.title}
      </h2>
      <dl class="ws-facts">
        {facts.map(([k, v, ltr]) => (
          <div key={k}>
            <dt>{t(k)}</dt>
            <dd dir={ltr ? 'ltr' : undefined}>{v}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function stateNote(s: StageView, status: PrDetailBody['status'], own: boolean): MessageKey {
  switch (s.state) {
    case 'done':
      return 'ws.state.done';
    case 'todo':
      return 'ws.state.todo';
    case 'skipped':
      return 'step.note.skipped';
    default:
      return statusKey(status, own);
  }
}

/** One tab per stage; the stage the request is in is filled, the page you are on underlined. */
export function StageTabs({
  pr,
  viewer,
  active,
}: {
  pr: PrDetailBody;
  viewer: Viewer;
  active: Stage;
}) {
  const { t } = useI18n();
  const stages = stagesFor(pr.status, viewer, pr.hasPo);
  return (
    <nav class="stage-tabs" aria-label={t('ws.stages')}>
      <ol style={{ '--stages': String(stages.length) }}>
        {stages.map((s, i) => {
          const body = (
            <>
              <span class="stage-tab__name">
                <span class="stage-tab__no" dir="ltr" aria-hidden="true">
                  {s.state === 'done' ? '✓' : String(i + 1)}
                </span>
                {t(`ws.stage.${s.key}` as MessageKey)}
              </span>
              <span class="stage-tab__note">{t(stateNote(s, pr.status, pr.canEdit))}</span>
            </>
          );
          const cls = `stage-tab stage-tab--${s.state}${s.key === active ? ' stage-tab--active' : ''}`;
          return (
            <li key={s.key}>
              {s.href === null ? (
                <span class={cls}>{body}</span>
              ) : (
                <a
                  class={cls}
                  href={`#/requests/${pr.prNo}${s.href}`}
                  aria-current={s.key === active ? 'page' : undefined}
                >
                  {body}
                </a>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function actionHref(a: ActionId, pr: PrDetailBody): string {
  const base = `#/requests/${pr.prNo}`;
  switch (a) {
    case 'edit':
      return `#/requests/draft/${pr.prNo}`;
    case 'review':
      return '#/review';
    case 'quotes':
      return `${base}/quotes`;
    case 'rfq':
      return `${base}/rfq`;
    case 'po':
      return `${base}/po`;
    case 'cfo':
      return `#/po-approvals/${pr.prNo}`;
    case 'delivery':
    case 'deliveryView':
      return `${base}/delivery`;
    case 'invoice':
      return `${base}/invoice`;
    case 'payments':
      return `${base}/payments`;
    case 'poDoc':
      return `#/po/${pr.poNo}`;
    case 'headDecide':
      return '#head-decision';
  }
}

const BUTTON: Record<ActionId, MessageKey> = {
  edit: 'home.action.continue',
  headDecide: 'ws.btn.headDecide',
  review: 'ws.btn.review',
  quotes: 'quote.open',
  rfq: 'rfq.open',
  po: 'po.open',
  cfo: 'cfo.openAsCfo',
  delivery: 'dl.open',
  deliveryView: 'dl.view',
  invoice: 'inv.open',
  payments: 'pv.payments',
  poDoc: 'pod.open',
};

function headline(step: NextStep, pr: PrDetailBody): MessageKey {
  if (step.primary === 'edit')
    return pr.status === 'Returned' ? 'ws.do.editReturned' : 'ws.do.editDraft';
  if (step.primary) return `ws.do.${step.primary}` as MessageKey;
  return `ws.wait.${step.owner}` as MessageKey;
}

/** The one thing to do next, with at most one main button (design pass, artboard 1). */
export function NextStepBar({
  pr,
  step,
  children,
}: {
  pr: PrDetailBody;
  step: NextStep;
  children?: ComponentChildren;
}) {
  const { t } = useI18n();
  const label = (a: ActionId) =>
    a === 'edit' && pr.status === 'Returned' ? t('req.editResubmit') : t(BUTTON[a]);
  return (
    <section
      class={`next-bar${step.primary ? ' next-bar--yours' : ''}`}
      aria-labelledby="next-bar-title"
    >
      <div class="next-bar__text">
        <span class="next-bar__label">
          {step.primary ? t('ws.next.yours') : t('ws.next.where')}
        </span>
        <span id="next-bar-title" class="next-bar__title">
          {t(headline(step, pr))}
        </span>
      </div>
      <div class="actions next-bar__actions">
        {step.secondary.map((a) => (
          <a key={a} class="button" href={actionHref(a, pr)}>
            {label(a)}
            {a === 'poDoc' && (
              <span class="button__ref" dir="ltr">
                {pr.poNo}
              </span>
            )}
          </a>
        ))}
        {children}
        {step.primary && (
          <a class="button button--primary" href={actionHref(step.primary, pr)}>
            {label(step.primary)}
          </a>
        )}
      </div>
    </section>
  );
}

/**
 * Wraps a stage page (quotes, PO, delivery, invoice, payments) in the request's header and stage
 * tabs. Reads the request once; if that fails the stage page still shows on its own.
 */
export function RequestFrame({
  prNo,
  active,
  roles,
  children,
}: {
  prNo: string;
  active: Stage;
  roles: readonly string[];
  children: ComponentChildren;
}) {
  const [pr, setPr] = useState<PrDetailBody | null>(null);
  useEffect(() => {
    let live = true;
    apiGet<PrDetailBody>(`/api/prs/${encodeURIComponent(prNo)}/detail`)
      .then((body) => live && setPr(body))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [prNo]);
  const viewer: Viewer | null = pr
    ? {
        staff: pr.staffView,
        procurement: roles.includes('Procurement'),
        cfo: roles.includes('CFO'),
      }
    : null;
  return (
    <div class="stack ws-frame">
      {pr && viewer && (
        <>
          <RequestHeader pr={pr} />
          <StageTabs pr={pr} viewer={viewer} active={active} />
        </>
      )}
      <div class={pr ? 'ws-frame__body' : undefined}>{children}</div>
    </div>
  );
}
